import type { InputHTMLAttributes, ReactNode } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { cn } from '../../lib/cn';
import { idCampo } from './idCampo';

/**
 * I pezzi del modulo pubblico di iscrizione ai corsi. Riprendono l'impianto
 * del modulo di registrazione di Safety Expo (sezioni con fascia grigia,
 * etichetta sopra il campo, Si'/No affiancati, avvisi gialli) con i colori
 * di SLAB.
 *
 * I campi sono a 16px: sotto quella misura l'iPhone ingrandisce la pagina
 * ogni volta che si tocca una casella.
 */

const CLASSE_CAMPO =
  'block w-full rounded-none border bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 ' +
  'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500';

function classeCampo(errore?: string): string {
  return cn(CLASSE_CAMPO, errore ? 'border-red-500 bg-red-50/40' : 'border-gray-300');
}

function idErrore(campo: string): string {
  return `iscr-${campo}-errore`;
}

type SezioneProps = {
  titolo: string;
  children: ReactNode;
};

export function Sezione({ titolo, children }: SezioneProps) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="bg-gray-100 px-4 py-3 text-xl font-bold text-gray-600 sm:text-2xl">{titolo}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

export function NotaObbligatori() {
  return <p className="mb-3 text-base text-red-600">* Tutti i campi sono obbligatori</p>;
}

type CampoProps = {
  campo: string;
  etichetta: string;
  obbligatorio?: boolean;
  errore?: string;
  children: ReactNode;
};

/** Etichetta sopra, campo, errore sotto. */
export function Campo({ campo, etichetta, obbligatorio = true, errore, children }: CampoProps) {
  return (
    <div className="mb-3">
      <label htmlFor={idCampo(campo)} className="mb-1 block text-lg text-gray-900">
        {etichetta}
        {obbligatorio && <span className="ml-1 align-top text-sm text-gray-500">*</span>}
      </label>
      {children}
      <MessaggioErrore campo={campo} errore={errore} />
    </div>
  );
}

function MessaggioErrore({ campo, errore }: { campo: string; errore?: string }) {
  if (!errore) return null;
  return (
    <p id={idErrore(campo)} role="alert" className="mt-1 text-sm font-medium text-red-600">
      {errore}
    </p>
  );
}

type CasellaTestoProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'onChange' | 'value'> & {
  campo: string;
  valore: string;
  errore?: string;
  onCambia: (valore: string) => void;
};

export function CasellaTesto({ campo, valore, errore, onCambia, className, ...altro }: CasellaTestoProps) {
  return (
    <input
      id={idCampo(campo)}
      name={campo}
      value={valore}
      onChange={(e) => onCambia(e.target.value)}
      aria-invalid={Boolean(errore)}
      aria-describedby={errore ? idErrore(campo) : undefined}
      className={cn(classeCampo(errore), className)}
      {...altro}
    />
  );
}

type Voce = { valore: string; testo: string };

type MenuProps = {
  campo: string;
  valore: string;
  voci: readonly (string | Voce)[];
  errore?: string;
  segnaposto?: string;
  disabilitato?: boolean;
  onCambia: (valore: string) => void;
};

/** Menu a tendina nativo: sul telefono apre la ruota del sistema, la piu' comoda. */
export function Menu({ campo, valore, voci, errore, segnaposto = 'Seleziona', disabilitato, onCambia }: MenuProps) {
  return (
    <div className="relative">
      <select
        id={idCampo(campo)}
        name={campo}
        value={valore}
        disabled={disabilitato}
        onChange={(e) => onCambia(e.target.value)}
        aria-invalid={Boolean(errore)}
        aria-describedby={errore ? idErrore(campo) : undefined}
        className={cn(classeCampo(errore), 'appearance-none pr-10 disabled:bg-gray-100 disabled:text-gray-400', !valore && 'text-gray-400')}
      >
        <option value="" disabled>
          {segnaposto}
        </option>
        {voci.map((voce) => {
          const { valore: v, testo } = typeof voce === 'string' ? { valore: voce, testo: voce } : voce;
          return (
            <option key={v} value={v} className="text-gray-900">
              {testo}
            </option>
          );
        })}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
    </div>
  );
}

type SceltaSiNoProps = {
  campo: string;
  valore: '' | 'si' | 'no';
  errore?: string;
  /** Testo della domanda, quando non e' gia' il titolo della sezione. */
  domanda?: string;
  onCambia: (valore: 'si' | 'no') => void;
};

/** Si' e No affiancati, come nel modulo originale. Nessuno dei due e' gia' scelto. */
export function SceltaSiNo({ campo, valore, errore, domanda, onCambia }: SceltaSiNoProps) {
  const opzioni = [
    { v: 'si', testo: 'Sì' },
    { v: 'no', testo: 'No' },
  ] as const;

  return (
    <fieldset className="my-4" aria-describedby={errore ? idErrore(campo) : undefined}>
      {domanda ? (
        <legend className="mb-2 text-lg leading-relaxed text-gray-900">{domanda}</legend>
      ) : (
        <legend className="sr-only">Scegli Sì o No</legend>
      )}
      <div className="flex gap-8">
        {opzioni.map(({ v, testo }, indice) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 text-lg text-gray-900">
            <input
              id={indice === 0 ? idCampo(campo) : undefined}
              type="radio"
              name={campo}
              value={v}
              checked={valore === v}
              onChange={() => onCambia(v)}
              className="h-6 w-6 cursor-pointer accent-primary-600"
            />
            {testo}
          </label>
        ))}
      </div>
      <MessaggioErrore campo={campo} errore={errore} />
    </fieldset>
  );
}

type AvvisoAttestatoProps = {
  /** Cosa non e' stato richiesto: "CFP", "ORE AGGIORNAMENTO 81/08". */
  cosa: string;
};

export function AvvisoAttestato({ cosa }: AvvisoAttestatoProps) {
  return (
    <div className="my-4 flex gap-3 rounded border border-amber-200 bg-amber-50 px-4 py-4 text-lg leading-relaxed text-amber-900">
      <Info className="mt-1 h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <p>
        Attenzione: non hai richiesto <strong>{cosa}</strong> e quindi non riceverai alcun attestato.
        <br />
        Puoi cambiare la tua scelta ma <strong>SOLO prima dell'inizio del corso</strong>, scrivendoci.
      </p>
    </div>
  );
}
